import { Layout } from 'antd';
import { ErpPermissionGroup, PartnersActions } from 'collections/authorization';
import lookers from 'collections/lookers';
import { AntRow } from 'components/ant/AntRow';
import { AuthorizedRoute } from 'controllers/AuthorizedRoute';
import {
  IncomingInvoicesPage,
  JobOffersListPage,
  LookerPage,
  PartnerBalancePage,
  ProviderAvailabilityCalendarPage,
  ProviderContactsPage,
  ProviderEditPage,
  ProviderOrdersPage,
  RfqOffersPage,
  ShippingsListPage,
} from 'pages';
import React, { FC } from 'react';
import { Redirect, Route, Switch, useParams } from 'react-router-dom';
import { routes } from 'routes';
import { useProviderQuery } from 'utils/graphql/queries/__generated__/provider';

import { Header } from './Header';
import { ColCenterContent, ColLeftSidebar, ColRightSidebar } from './Shared.styled';
import { Sidebar } from './Sidebar';
import SidebarRightProvider from './SidebarRightProvider';

interface Params {
  providerId: string;
}

const ProviderController: FC = () => {
  const { providerId } = useParams<Params>();

  const { loading, data } = useProviderQuery({
    variables: { id: providerId },
  });

  if (loading || !data?.provider) {
    return <></>;
  }

  const { provider } = data;

  document.title = `PR-${provider.id} | ERP`;

  return (
    <Layout.Content style={{ height: '100vh' }}>
      <Header provider={provider} />
      <AntRow style={{ height: '100%' }}>
        <ColLeftSidebar xs={24} sm={4} xxl={3}>
          <Sidebar provider={provider} />
        </ColLeftSidebar>

        <ColCenterContent xs={24} sm={16} xxl={18}>
          <div className="inner-content">
            <Switch>
              <AuthorizedRoute
                group={ErpPermissionGroup.Partners}
                action={PartnersActions.PartnerView}
                path={`${routes.providerPath(':providerId')}/edit`}
              >
                <ProviderEditPage provider={provider} />
              </AuthorizedRoute>
              <AuthorizedRoute
                group={ErpPermissionGroup.Partners}
                action={PartnersActions.PartnerView}
                path={`${routes.providerPath(':providerId')}/contacts`}
              >
                <ProviderContactsPage provider={provider} />
              </AuthorizedRoute>
              <Route
                path={`${routes.providerPath(':providerId')}/production_orders`}
                exact
                render={() => <ProviderOrdersPage initialFilter={`provider_id = '${providerId}'`} embed />}
              />
              <Route
                path={`${routes.providerPath(':providerId')}/invoices`}
                render={() => <IncomingInvoicesPage initialFilter={`partner_id = '${providerId}'`} embed />}
              />
              <AuthorizedRoute
                group={ErpPermissionGroup.Partners}
                action={PartnersActions.PartnerView}
                path={`${routes.providerPath(':providerId')}/rating_by_parts`}
              >
                <LookerPage url={lookers.partners.ratingByParts(providerId)} title="ERP | Partners | Rating by parts" />
              </AuthorizedRoute>
              <Route
                path={`${routes.providerPath(':providerId')}/shippings`}
                render={() => <ShippingsListPage initialFilter={`related_providers CONTAINS '${providerId}'`} embed />}
              />
              <Route
                path={`${routes.providerPath(':providerId')}/job_offers`}
                exact
                render={() => (
                  <JobOffersListPage
                    embed
                    initialFilter={`provider_id = '${providerId}' AND (response_state = 'application' OR response_state = 'counteroffer')`}
                  />
                )}
              />
              <Route
                path={`${routes.providerPath(':providerId')}/rfq_offers`}
                exact
                render={() => <RfqOffersPage initialFilter={`provider_id = '${providerId}'`} embed />}
              />
              <AuthorizedRoute
                group={ErpPermissionGroup.Partners}
                action={PartnersActions.PartnerView}
                path={`${routes.providerPath(':providerId')}/availability_calendar`}
                exact
              >
                <ProviderAvailabilityCalendarPage provider={provider} />
              </AuthorizedRoute>

              <Route
                path={routes.providerBalancePath(':providerId')}
                render={() => (
                  <Switch>
                    <AuthorizedRoute
                      group={ErpPermissionGroup.Partners}
                      action={PartnersActions.PartnerView}
                      path={`${routes.providerBalancePath(':providerId')}/balance`}
                      exact
                    >
                      <PartnerBalancePage currentTab="balance" provider={provider} />
                    </AuthorizedRoute>
                    <AuthorizedRoute
                      group={ErpPermissionGroup.Partners}
                      action={PartnersActions.PartnerView}
                      path={`${routes.providerBalancePath(':providerId')}/transactions`}
                      exact
                    >
                      <PartnerBalancePage currentTab="transactions" provider={provider} />
                    </AuthorizedRoute>
                    <AuthorizedRoute
                      group={ErpPermissionGroup.Partners}
                      action={PartnersActions.PartnerView}
                      path={`${routes.providerBalancePath(':providerId')}/statement`}
                      exact
                    >
                      <PartnerBalancePage currentTab="statement" provider={provider} />
                    </AuthorizedRoute>
                    <Redirect to={`${routes.providerBalancePath(':providerId')}/balance`} />
                  </Switch>
                )}
              />

              <Redirect to={`${routes.providerPath(':providerId')}/edit`} />
            </Switch>
          </div>
        </ColCenterContent>

        <ColRightSidebar xs={24} sm={4} xxl={3}>
          <SidebarRightProvider />
        </ColRightSidebar>
      </AntRow>
    </Layout.Content>
  );
};

export default ProviderController;
